import Typography from '@mui/material/Typography';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'shared/assets/icons';
import { IconButton, Sizes } from 'shared/components/ui/icon-button';
import { KeyboardShortcut } from 'shared/components/ui/keyboard-shortcut/keyboard-shortcut';
import { useElectron } from 'shared/hooks/use-is-mac';

import * as Styled from './shortcut-input.style';

export type ShortcutInputProps = {
  label?: string;
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  id?: string;
  helperText?: string;
};

const mapKey = (key: string, isMac: boolean): string => {
  if (key === ' ') {
    return isMac ? '␣' : 'Space';
  }
  if (key === 'Control') {
    return isMac ? '⌃' : 'Ctrl';
  }
  if (key === 'Command' || key === 'Meta') {
    return isMac ? '⌘' : 'Cmd';
  }
  if (key === 'Alt' || key === 'Option') {
    return isMac ? '⌥' : 'Alt';
  }
  if (key === 'Shift') {
    return isMac ? '⇧' : 'Shift';
  }
  if (key === 'Enter') {
    return isMac ? '⏎' : 'Enter';
  }
  // Capitalize single letters
  if (key.length === 1) {
    return key.toUpperCase();
  }
  return key;
};

export const ShortcutInput: React.FC<ShortcutInputProps> = ({
  label,
  value,
  onChange,
  placeholder,
  disabled,
  error,
  id,
  helperText,
}) => {
  const { t } = useTranslation();
  const { isMac } = useElectron();
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Track currently pressed keys
  const [recordingValue, setRecordingValue] = useState<string | null>(null);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab') {
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setRecordingValue(null);
      inputRef.current?.blur();
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    // Electron accelerator format: Modifiers+Key
    // Modifiers: Command, Control, Option, Shift, Alt
    // We need to map DOM keys to Electron format

    let key = e.key;

    // Normalizing keys
    if (key === ' ' || e.code === 'Space') {
      key = 'Space';
    }
    if (key === 'Meta') {
      key = 'Command';
    }
    if (key === 'Control') {
      key = 'Control';
    }
    if (key === 'Alt') {
      key = 'Option';
    }
    if (key === 'Shift') {
      key = 'Shift';
    }

    const modifiers: string[] = [];
    if (e.shiftKey) {
      modifiers.push('Shift');
    }
    if (e.ctrlKey) {
      modifiers.push('Control');
    }
    if (e.altKey) {
      modifiers.push('Option');
    }
    if (e.metaKey) {
      modifiers.push('Command');
    }

    if (!modifiers.length) {
      return;
    }

    // If the key itself is a modifier, we don't add it as the "main" key yet
    const isModifier = [
      'Command',
      'Control',
      'Option',
      'Shift',
      'Meta',
      'Alt',
    ].includes(key);

    let finalKey = '';
    if (!isModifier) {
      finalKey = key.length === 1 ? key.toUpperCase() : key;
    }

    // Sort modifiers order: Control+Option+Shift+Command is a standard canonical order or similar.
    // Electron usually prefers: CommandOrControl+Option+Shift+Key
    // Let's stick to the order we pushed them or simple sort.

    // Remove duplicates from modifiers (e.g. if key was Meta, it's already in modifiers)
    const uniqueParts = Array.from(new Set([...modifiers, finalKey])).filter(
      Boolean,
    );

    const shortcutString = uniqueParts.join('+');

    if (shortcutString) {
      setRecordingValue(shortcutString);
      if (!isModifier) {
        onChange(shortcutString);
      }
    }
  };

  const clearShortcut = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setRecordingValue(null);
    inputRef.current?.focus();
  };

  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  const displayValue = recordingValue ?? value;
  const shortcutParts = displayValue
    ? displayValue.split('+').map((k) => mapKey(k, isMac))
    : [];

  return (
    <Styled.Container>
      {!!label && <Styled.Label htmlFor={id}>{label}</Styled.Label>}
      <Styled.ContentContainer>
        <Styled.InputContainer onClick={handleContainerClick}>
          <Styled.HiddenInput
            ref={inputRef}
            id={id}
            disabled={disabled}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              setFocused(false);
              setRecordingValue(null);
            }}
            onKeyDown={handleKeyDown}
            readOnly
          />
          <Styled.ShortcutDisplay
            $focused={focused}
            $hasError={!!error}
            $disabled={!!disabled}
          >
            {shortcutParts.length ? (
              <KeyboardShortcut shortcut={shortcutParts} />
            ) : (
              <Styled.Placeholder>{placeholder}</Styled.Placeholder>
            )}
          </Styled.ShortcutDisplay>

          <Styled.EndContainer>
            {value && !disabled && (
              <IconButton
                onClick={clearShortcut}
                aria-label={t('general.labels.clear-field')}
                icon={X}
                size={Sizes.Small}
              />
            )}
          </Styled.EndContainer>
        </Styled.InputContainer>
        {!!helperText && (
          <Typography
            variant="overline"
            component="span"
            color="text.secondary"
          >
            {helperText}
          </Typography>
        )}
      </Styled.ContentContainer>
    </Styled.Container>
  );
};
